import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Col, Input, Row, Space } from 'antd';
import { useEffect, useState } from 'react';
import { getClients } from '../../api';
import { components, paths } from '../../api/schema';
import DataTable, {
  ColumnsType,
  DataTableFilterCallback,
  DataTableOrderCallback,
  serializeFilters,
  serializeOrders,
} from '../../shared/dataTable/DataTable';
import useDebounce from '../../shared/hooks/useDebounce';
import PanelSkeleton from '../../shared/skeletons/PanelSkeleton';
import ClientDetail from './ClientDetail';
import CreateClient from './CreateClient';
import { DeleteClient } from './clientPanelActions/DeleteAction';
import { EditClient } from './clientPanelActions/EditAction';

const clientsTableColumns: ColumnsType<components['schemas']['ClientCondensed']> = [
  {
    title: 'Nombre',
    dataIndex: 'name',
    key: 'name',
    columnType: 'text',
  },
  {
    title: 'RFC',
    dataIndex: 'rfc',
    key: 'rfc',
    columnType: 'text',
  },

  {
    title: 'Correo',
    dataIndex: 'emailAddress',
    key: 'emailAddress',
    columnType: 'text',
  },
  {
    title: 'Teléfono',
    dataIndex: 'phoneNumber',
    key: 'phoneNumber',
    columnType: 'text',
  },
  {
    title: 'Horas de oficina',
    dataIndex: 'officeHours',
    key: 'officeHours',
    columnType: 'text',
  },
  {
    title: 'Crédito',
    dataIndex: 'credit',
    key: 'credit',
    columnType: 'boolean',
  },
  {
    title: 'Tipo persona',
    dataIndex: 'tipoPersona',
    key: 'tipoPersona',
    columnType: 'enum',
  },
];
const PAGE_SIZE = 50;

export default function ClientPanel() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>();
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce<string>(search, 300);
  const [params, setParams] = useState<paths['/client/list']['get']['parameters']['query']>({
    search: undefined,
    limit: PAGE_SIZE,
    page: 1,
    showDisabled: false,
    order: JSON.stringify([]),
    filter: JSON.stringify([]),
  });
  const { isLoading, isError, data, isFetching } = useQuery({
    queryKey: ['getClients', params],
    queryFn: () => getClients(params),
  });

  useEffect(() => {
    setParams((state) => ({ ...state, search: debouncedSearch }));
  }, [debouncedSearch]);

  const handleFiltering = (filter: DataTableFilterCallback) => {
    setParams((state) => ({ ...state, filter: serializeFilters(params.filter, filter) }));
  };

  const handleOrdering = (order: DataTableOrderCallback) => {
    setParams((state) => ({ ...state, order: serializeOrders(params.order, order) }));
  };

  const handlePageChange = (page: number) => {
    setParams((state) => ({ ...state, page }));
  };

  return (
    <Row>
      <Col span={24}>
        <Space size={'middle'}>
          <Button type="primary" onClick={() => setCreateModalOpen(true)}>
            Crear cliente
          </Button>
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            suffix={<SearchOutlined />}
          />
        </Space>
      </Col>
      {isLoading || isError || isFetching ? (
        <PanelSkeleton />
      ) : (
        <>
          <Col span={24} style={{ marginTop: '10px' }}>
            <DataTable<components['schemas']['ClientCondensed']>
              handleFilters={handleFiltering}
              handleOrder={handleOrdering}
              handlePageChange={handlePageChange}
              columns={clientsTableColumns}
              results={data.results}
              activeFilters={params.filter}
              activeOrders={params.order}
              paginationData={{
                pageSize: PAGE_SIZE,
                count: data.count,
                currentPage: data.currentPage,
              }}
              actions={[EditClient,DeleteClient]}
              onRowClick={(record) => {
                setSelectedClientId(record.id);
                setDetailModalOpen(true);
              }}
            />
          </Col>

          <ClientDetail clientId={selectedClientId} open={detailModalOpen} onCancel={() => setDetailModalOpen(false)} />
          <CreateClient
            open={createModalOpen}
            onCancel={() => setCreateModalOpen(false)}
            onCreate={() => setCreateModalOpen(false)}
          />
        </>
      )}
    </Row>
  );
}
